import {
    Component,
    ViewChild,
    AfterViewInit,
    OnDestroy,
    ChangeDetectorRef,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
    MatPaginator,
    MatPaginatorModule,
    PageEvent
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BufferProgressBarComponent } from '../../../ui-components/buffer-progress-bar/buffer-progress-bar.component';
import { SearchInputWithDebouncingComponent } from '../../../ui-components/search-input-with-debouncing/search-input-with-debouncing.component';
import { PlantsService } from '../services/plants.service';
import { Subject, takeUntil } from 'rxjs';
import { CityService } from '../services/city.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmPlantToggleComponent } from './confirm-plant-toggle/confirm-plant-toggle.component';
import { B2bDashboardService } from '../../b2b-dashboard/b2b-dashboard.service';

type RowModel = {
    id: number;
    plantOwner: string;
    cityName: string;
    userName: string;
    email: string;
    phoneNumber: string;
    customerType: string;
    disabled: boolean;
    userId: number;
    hasOfflineDevices: boolean;
};

@Component({
    selector: 'app-all-plant-list',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterLink,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatTooltipModule,
        MatIconModule,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        BufferProgressBarComponent,
        SearchInputWithDebouncingComponent,
        TranslatePipe
    ],
    templateUrl: './all-plant-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './all-plant-list.component.scss'
})
export class AllPlantListComponent implements AfterViewInit, OnDestroy {
    plantId!: number;
    displayedColumns: string[] = [
        'id',
        'plantOwner',
        'cityName',
        'userName',
        'email',
        'phoneNumber',
        'customerType',
        'disabled',
        'hasOfflineDevices',
        'action'
    ];
    activeConnectivityPlantId: number | null = null;
    readonly connectivityOverlayPositions: ConnectedPosition[] = [
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -10 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 10 }
    ];
    toggleBusyIds = new Set<number>();
    private offlineInvertersByPlant = new Map<number, string[]>();
    offlineDetailsLoading = false;
    private offlineDetailsLoaded = false;
    offlineDetailsUnavailable = false;

    constructor(
        private plantService: PlantsService,
        private router: Router,
        private cd: ChangeDetectorRef,
        private route: ActivatedRoute,
        private citySrc: CityService,
        private dialog: MatDialog,
        private dashboardService: B2bDashboardService,
        private translate: TranslateService
    ) {}

    dataSource = new MatTableDataSource<RowModel>([]);
    total = 0;
    isLoading = false;
    pageSize = 8;
    private destroy$ = new Subject<void>();
    private searchText = '';

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    ngAfterViewInit(): void {
        this.paginator.pageSize = this.pageSize;
        this.dataSource.sort = this.sort;
        this.loadPlants(0, this.paginator.pageSize);
        this.paginator.page
            .pipe(takeUntil(this.destroy$))
            .subscribe((e: PageEvent) =>
                this.loadPlants(e.pageIndex, e.pageSize)
            );
        this.cd.detectChanges();
    }

    onSearch(q: string) {
        this.searchText = (q || '').trim();
        this.paginator.firstPage();
        this.loadPlants(0, this.paginator.pageSize);
    }

    private loadPlants(pageIndex: number, pageSize: number) {
        this.isLoading = true;
        this.plantService
            .getPlants({
                searchText: this.searchText || undefined,
                page: pageIndex + 1,
                pageSize
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    const items = res?.plants ?? [];
                    const totalFromApi =
                        typeof res?.totalCount === 'number'
                            ? res.totalCount
                            : typeof res?.total === 'number'
                            ? res.total
                            : typeof res?.count === 'number'
                            ? res.count
                            : undefined;

                    this.total = totalFromApi ?? (this.total || items.length);

                    const rows: RowModel[] = items.map((p: any) => ({
                        id: p.id,
                        plantOwner: p.plantOwner,
                        cityId: p.cityId,
                        cityName: p.cityName,
                        userName: p.userName,
                        email: p.email,
                        phoneNumber: p.phoneNumber,
                        customerType: p.customerType,
                        disabled: p.disabled,
                        userId: p.userId,
                        hasOfflineDevices: p.hasOfflineDevices === true
                    }));

                    this.dataSource.data = rows;
                    this.isLoading = false;
                },
                error: () => {
                    this.dataSource.data = [];
                    this.total = 0;
                    this.isLoading = false;
                }
            });
    }

    /** Fetch serial numbers only when an offline status is inspected. */
    loadOfflineInverterDetails(): void {
        if (this.offlineDetailsLoaded || this.offlineDetailsLoading || this.offlineDetailsUnavailable) return;
        this.offlineDetailsLoading = true;
        this.dashboardService.getMainDashboardData().pipe(takeUntil(this.destroy$)).subscribe({
            next: response => {
                this.offlineInvertersByPlant.clear();
                for (const plant of response.plants ?? []) {
                    const serials = (plant.inverters ?? [])
                        .filter(inverter => inverter.isOnline === false)
                        .map(inverter => inverter.serialNumber)
                        .filter((serial): serial is string => !!serial);
                    this.offlineInvertersByPlant.set(plant.plantId, serials);
                }
                this.offlineDetailsLoaded = true;
                this.offlineDetailsLoading = false;
                this.cd.markForCheck();
            },
            error: () => {
                this.offlineDetailsUnavailable = true;
                this.offlineDetailsLoading = false;
                this.cd.markForCheck();
            }
        });
    }

    showConnectivityDetails(row: RowModel): void {
        this.activeConnectivityPlantId = row.id;
        if (row.hasOfflineDevices) this.loadOfflineInverterDetails();
    }

    hideConnectivityDetails(row: RowModel): void {
        if (this.activeConnectivityPlantId === row.id) this.activeConnectivityPlantId = null;
    }

    offlineInverterSerials(plantId: number): string[] {
        return this.offlineInvertersByPlant.get(plantId) ?? [];
    }

    offlineTooltip(row: RowModel): string {
        if (!row.hasOfflineDevices) return this.translate.instant('All_inverters_are_online');
        if (this.offlineDetailsLoading) return this.translate.instant('Loading_offline_inverter_details');
        if (!this.offlineDetailsLoaded) {
            return this.translate.instant(
                this.offlineDetailsUnavailable
                    ? 'Offline_inverter_details_unavailable'
                    : 'Offline_inverter_hover_details'
            );
        }
        const serials = this.offlineInvertersByPlant.get(row.id) ?? [];
        return serials.length
            ? `${this.translate.instant(serials.length > 1 ? 'Offline_inverters' : 'Offline_inverter')}:\n${serials.join('\n')}`
            : this.translate.instant('Offline_inverter_serials_unavailable');
    }

    onView(row: any) {
        const data = {
            id: row.id
        };
        const queryParams: any = {};
        queryParams.plantId = data.id;
        this.router
            .navigate(['/plants/plants-detail', data.id])
            .then(success => {
                if (!success) console.error('Navigation failed!');
            });
    }

    onDelete(row: RowModel) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onEdit(row: any) {
        const plantId = row.id;
        if (!plantId) {
            console.warn('No plant id resolved for update');
            return;
        }

        this.router.navigate(['/plants/update', plantId]);
    }

    onAddInverter(row: any) {
        const plantId = row?.id;
        if (!plantId) return;
        this.router.navigate(['/plants', plantId, 'inverters', 'add']);
    }

    onToggleDisablePlant(row: RowModel): void {
        if (!row?.id || this.toggleBusyIds.has(row.id)) return;

        const ref = this.dialog.open(ConfirmPlantToggleComponent, {
            data: { plantOwner: row.plantOwner, disabled: row.disabled }
        });

        ref.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(confirmed => {
                if (!confirmed) return;

                this.toggleBusyIds.add(row.id);
                const previousDisabled = row.disabled;

                row.disabled = !previousDisabled;
                this.cd.detectChanges();

                this.plantService
                    .toggleDisablePlant(row.id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (res: any) => {
                            const serverDisabled = res?.currentStatus;
                            row.disabled =
                                typeof serverDisabled === 'boolean'
                                    ? serverDisabled
                                    : !previousDisabled;
                            this.toggleBusyIds.delete(row.id);
                            this.cd.detectChanges();
                        },
                        error: () => {
                            row.disabled = previousDisabled;
                            this.toggleBusyIds.delete(row.id);
                            this.cd.detectChanges();
                        }
                    });
            });
    }
}
